/**
 * Ryan O'Dowd
 * 2023-01-19
 * © Copyright 2023 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Linking,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useState,
} from 'react';
import Button from '@components/common/Button';
import Dialog from '@components/common/Dialog';
import RenderHtml from 'react-native-render-html';
import Text from '@components/common/Text';
import getStyles from './styles';
import hooks from '@hooks';

const LicensingInfo = (props: LicensingInfoProps) => {
  const styles = hooks.useStyles(getStyles);
  const i18n = hooks.useI18n();

  const [_isDialogOpen, setIsDialogOpen] = useState(false);

  const translation = hooks.useTypedSelector((state) => {
    return state.translations.find((t) => t.id === props.translationId);
  }, (oldValue, newValue) => JSON.stringify(oldValue) === JSON.stringify(newValue));

  const name = `${translation?.name} (${translation?.abbreviation})`;

  const learnMoreButton = translation.url ? (
    <Text
      style={styles.learnMoreButtonText}
      onPress={() => Linking.openURL(translation.url)}
    >
      {i18n('learnMore')}
    </Text>
  ) : null;

  return (
    <View style={styles.container}>
      {/* @TODO: this should be a lot prettier...put most in dialog? hide long text behind cards with basic info? */}
      <TouchableOpacity
        style={styles.touchable}// @TODO: don't apply these styles in read mode
        disabled={!(translation.copyright_long || translation.info)}
        onPress={() => setIsDialogOpen(true)}
      >
        <Text style={[styles.text, styles.titleText]}>{name}</Text>
        <Text style={styles.text}>{translation.copyright_short}  {learnMoreButton}</Text>{/* @TODO: on web, learn more is row instead of inline */}
      </TouchableOpacity>
      <Dialog
        visible={_isDialogOpen}
        onDismiss={() => setIsDialogOpen(false)}
      >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
        {/* @TODO: should settings be tab-navigated, grouped by category? */}
        <Dialog.Title>{name}</Dialog.Title>
        <Dialog.Content>
          {translation.copyright_long &&
            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionTitle}>{i18n('copyright')}</Text>
              <RenderHtml
                contentWidth={1000}
                systemFonts={[styles.fonts.primary, styles.fonts.secondary]}
                idsStyles={{html: styles.dialogText}}
                source={{html: `<span id='html'>${translation.copyright_long.replace('\n', '<br /><br />')}</span>`}}
              />{/* @TODO: width is bad */}
            </View>
          }
          {translation.info &&
            <View style={styles.sectionWrapper}>
              <Text style={styles.sectionTitle}>{i18n('info')}</Text>
              <RenderHtml
                contentWidth={1000}
                systemFonts={[styles.fonts.primary, styles.fonts.secondary]}
                idsStyles={{html: styles.dialogText}}
                source={{html: `<span id='html'>${translation.info}</span>`}}
              />{/* @TODO: width is bad */}
            </View>
          }
          {translation.url &&
            <Button
              mode='outlined'
              textColor={styles.isDarkMode ? styles.colors.white : styles.colors.black}// @TODO: shouldn't need this once colors.js is cleaned up
              onPress={() => Linking.openURL(translation.url)}
            >
              {i18n('learnMore')}
            </Button>
          }
        </Dialog.Content>
        <Dialog.Actions>
          <Button
            mode='contained'
            onPress={() => setIsDialogOpen(false)}
          >
            {i18n('close')}
          </Button>
        </Dialog.Actions>
      </Dialog>
    </View>
  );
};

class LicensingInfoProps {
  translationId: number;
};

export default LicensingInfo;
